import { MessageSchema } from "@redotech/redo-model/agents/llm-messages";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import { ThreadEscalationInfoSchema } from "../merchant-support-agent/message-merchant-support-agent";

/**
 * Thread history shape returned to the Ask Redo UI. This is a
 * minimal projection of `LlmThread` — just what the history dropdown
 * needs to render a row (id, updatedAt, preview text). The full
 * `messages` array is intentionally NOT included here:
 *
 *   1. Strict `MessageSchema` validation rejects legacy/in-progress
 *      message shapes stored on older threads, killing the whole
 *      response payload.
 *   2. The dropdown only renders a one-line preview + date — it
 *      never iterates messages. Sending them was pure waste.
 *
 * To hydrate a full conversation, use `getAskRedoThread` instead
 * (single-thread fetch with permissive parsing on the client side).
 */
export const AskRedoThreadHistorySchema = z.object({
  _id: zExt.objectId(),
  updatedAt: z.date(),
  // A short preview derived from the most recent user message (used
  // in the thread list sidebar).
  preview: z.string().optional(),
  // Active or most-recent escalation linked to this thread. Powers
  // the status badge in the thread list and hydrates the escalation
  // context on reopen.
  escalation: ThreadEscalationInfoSchema.optional(),
});

export const getAskRedoThreads = {
  name: "getAskRedoThreads" as const,
  input: z.object({
    userEmail: z.string().email().describe("User email to filter threads by"),
  }),
  output: z.object({ threads: z.array(AskRedoThreadHistorySchema) }),
};

export const getAskRedoThread = {
  name: "getAskRedoThread" as const,
  input: z.object({ threadId: zExt.objectId() }),
  output: z.object({
    thread: z.object({
      _id: zExt.objectId(),
      updatedAt: z.date(),
      messages: z.array(MessageSchema),
      escalation: ThreadEscalationInfoSchema.optional(),
    }),
  }),
};
